/**
 * HumanFigure: A glowing stick-figure mannequin at the playhead (Z=0)
 * with 2-bone IK arms that track upcoming notes per hand.
 *
 * Features:
 *   - Left arm (blue) follows left_hand notes
 *   - Right arm (pink) follows right_hand notes
 *   - Hands follow rail paths when notes have rails
 *   - Green (one_hand_special) notes: one random hand per sequence
 *   - Both_hands_special (yellow): both hands track to same position
 *   - Subtle beat-synced body dance (head bob, hip sway, spine flex)
 *   - Obstacle dodge: leans left/right to avoid wall obstacles, ducks for center/crouch
 *
 * All animation is imperative (refs + useFrame) for 60fps performance.
 */

import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useEditorStore, getNoteColorsFromSettings } from '../../store/editorStore';
import type { MannequinSettings } from '../../store/editorStore';
import { GRID_SCALE } from '../../types/beatmap';
import type { Note, Slide } from '../../types/beatmap';

// ── Constants (must match TunnelScene.tsx) ──────────────────────────────────

const NOTE_Y_OFFSET = 1.8;

// ── Computed body proportions from settings ─────────────────────────────────

interface ComputedBody {
  headRadius: number; headY: number; neckY: number;
  shoulderY: number; shoulderWidth: number;
  hipY: number; hipWidth: number; kneeY: number;
  torsoRadius: number; upperArmLength: number; lowerArmLength: number;
  handRadius: number; figureZ: number;
}

/**
 * Compute body proportions from mannequin settings.
 * figureHeight is a multiplier (1.0 = default), scales all Y positions proportionally.
 */
function computeBody(s: MannequinSettings): ComputedBody {
  const h = s.figureHeight;
  return {
    headRadius: s.headRadius,
    headY: 1.78 * h,
    neckY: 1.68 * h,
    shoulderY: 1.55 * h,
    shoulderWidth: s.shoulderWidth,
    hipY: 0.9 * h,
    hipWidth: 0.15 * h,
    kneeY: 0.45 * h,
    torsoRadius: s.torsoRadius,
    upperArmLength: s.upperArmLength,
    lowerArmLength: s.lowerArmLength,
    handRadius: s.handRadius,
    figureZ: 0.12,
  };
}

// Default rest positions when no note is upcoming (arms relaxed at sides)
const REST_LEFT = new THREE.Vector3(-0.45, 1.1, 0.15);
const REST_RIGHT = new THREE.Vector3(0.45, 1.1, 0.15);

// Reusable temp vectors (avoid per-frame allocations)
const _yAxis = new THREE.Vector3(0, 1, 0);
const _tempDir = new THREE.Vector3();
const _tempQuat = new THREE.Quaternion();

// ── 2-Bone IK Solver ───────────────────────────────────────────────────────

interface IKResult {
  elbow: THREE.Vector3;
  hand: THREE.Vector3;
}

/**
 * Analytical 2-bone IK using law of cosines.
 * Elbow bends outward from body center and slightly forward.
 */
function solveTwoBoneIK(
  shoulder: THREE.Vector3,
  target: THREE.Vector3,
  upperLen: number,
  lowerLen: number,
  isLeftArm: boolean,
): IKResult {
  const maxReach = upperLen + lowerLen;
  const dir = new THREE.Vector3().subVectors(target, shoulder);
  let dist = dir.length();

  // Clamp to reachable range
  if (dist > maxReach * 0.999) {
    dist = maxReach * 0.999;
    dir.normalize().multiplyScalar(dist);
  }
  if (dist < Math.abs(upperLen - lowerLen) + 0.001) {
    dist = Math.abs(upperLen - lowerLen) + 0.001;
    dir.normalize().multiplyScalar(dist);
  }

  const handPos = new THREE.Vector3().addVectors(
    shoulder,
    dir.clone().normalize().multiplyScalar(dist),
  );

  // Law of cosines: angle at shoulder
  const cosAngle = (upperLen * upperLen + dist * dist - lowerLen * lowerLen)
    / (2 * upperLen * dist);
  const angle = Math.acos(THREE.MathUtils.clamp(cosAngle, -1, 1));

  // Pole vector hint: elbow bends outward + slightly forward + slightly down
  const outward = new THREE.Vector3(isLeftArm ? -1 : 1, 0, 0);
  const poleHint = new THREE.Vector3()
    .addScaledVector(outward, 0.7)
    .addScaledVector(new THREE.Vector3(0, 0, 1), 0.3)    // toward camera
    .addScaledVector(new THREE.Vector3(0, -1, 0), 0.2)   // slightly down
    .normalize();

  // Build coordinate frame along shoulder→target
  const dirNorm = dir.clone().normalize();
  const perp = new THREE.Vector3().crossVectors(dirNorm, poleHint).normalize();
  const perp2 = new THREE.Vector3().crossVectors(perp, dirNorm).normalize();

  // Elbow position
  const elbowOffset = new THREE.Vector3()
    .addScaledVector(dirNorm, upperLen * Math.cos(angle))
    .addScaledVector(perp2, upperLen * Math.sin(angle));

  const elbow = new THREE.Vector3().addVectors(shoulder, elbowOffset);

  return { elbow, hand: handPos };
}

// ── Note Lookup ─────────────────────────────────────────────────────────────

/**
 * Binary search for the start index near a target beat.
 */
function binarySearchBeat(notes: Note[], targetBeat: number): number {
  let lo = 0;
  let hi = notes.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    if (notes[mid].time_beats < targetBeat) lo = mid + 1;
    else hi = mid - 1;
  }
  return lo;
}

/**
 * Find the next note for a given hand relative to currentBeat.
 * Looks from (currentBeat - anticipationBeats) up to 4 beats ahead.
 *
 * Green notes (one_hand_special) are only matched if greenHand matches
 * the requested hand (or 'none' if not yet assigned).
 */
function findNextNoteForHand(
  notes: Note[],
  currentBeat: number,
  hand: 'left' | 'right',
  anticipationBeats: number,
  greenHand: 'left' | 'right' | 'none',
): Note | null {
  // Small look-behind (0.15 beats) so the hand stays on a note just long
  // enough for a visual "hit" before moving on.  Look-ahead is generous
  // so the hand starts reaching early.
  const searchStart = currentBeat - 0.15;
  const searchEnd = currentBeat + anticipationBeats + 4;

  const startIdx = binarySearchBeat(notes, searchStart);

  for (let i = startIdx; i < notes.length; i++) {
    const note = notes[i];
    if (note.time_beats > searchEnd) break;

    // Regular hand-specific notes
    if (note.hand === hand) return note;

    // Both-hands special: both hands go to it
    if (note.note_type === 'both_hands_special') return note;

    // Green (one_hand_special): only the assigned hand tracks it
    if (note.note_type === 'one_hand_special') {
      if (greenHand === hand || greenHand === 'none') return note;
      // Other hand ignores green notes entirely
    }
  }

  return null;
}

/**
 * Find a note whose rail is currently active for a given hand.
 * A rail is "active" when the note head is in the past but the last rail node's
 * beat is still in the future. This catches rails that the playhead is partway
 * through — notes that findNextNoteForHand misses because their time_beats is
 * behind the search window.
 *
 * Scans backward from the playhead position to avoid scanning the entire array.
 */
function findActiveRailNote(
  notes: Note[],
  currentBeat: number,
  hand: 'left' | 'right',
  bpm: number,
  greenHand: 'left' | 'right' | 'none',
): Note | null {
  // Start from where notes at currentBeat would be, and scan backward
  const startIdx = binarySearchBeat(notes, currentBeat);

  // Scan backward — rails can be long but typically < 8 beats
  const maxLookback = 16; // beats
  for (let i = startIdx - 1; i >= 0; i--) {
    const note = notes[i];

    // Don't look further back than maxLookback beats
    if (currentBeat - note.time_beats > maxLookback) break;

    // Skip notes without rails
    if (!note.has_rail || !note.rail_nodes || note.rail_nodes.length === 0) continue;

    // Check if this note's hand matches
    const matchesHand =
      note.hand === hand ||
      note.note_type === 'both_hands_special' ||
      (note.note_type === 'one_hand_special' && (greenHand === hand || greenHand === 'none'));

    if (!matchesHand) continue;

    // Check if the rail extends past currentBeat
    const lastNode = note.rail_nodes[note.rail_nodes.length - 1];
    const lastNodeTimeMs = lastNode[2] / 0.02;
    const lastNodeBeat = (lastNodeTimeMs / 1000.0) * (bpm / 60.0);

    if (lastNodeBeat > currentBeat) {
      // Rail is still active!
      return note;
    }
  }

  return null;
}

/**
 * Find the first upcoming green note to determine which hand should be assigned.
 * Returns the green note or null.
 */
function findNextGreenNote(
  notes: Note[],
  currentBeat: number,
  anticipationBeats: number,
): Note | null {
  const searchStart = currentBeat - 0.15;
  const searchEnd = currentBeat + anticipationBeats + 4;
  const startIdx = binarySearchBeat(notes, searchStart);

  for (let i = startIdx; i < notes.length; i++) {
    const note = notes[i];
    if (note.time_beats > searchEnd) break;
    if (note.note_type === 'one_hand_special') return note;
  }
  return null;
}

/**
 * Convert note position to world coordinates at the playhead (Z=0).
 * Same math as NoteMesh in TunnelScene.tsx.
 */
function noteToWorld(note: Note): THREE.Vector3 {
  return new THREE.Vector3(
    note.position[0] * GRID_SCALE * 10,
    note.position[1] * GRID_SCALE * 10 + NOTE_Y_OFFSET,
    0,
  );
}

// ── Obstacle Lean Lookup ────────────────────────────────────────────────────

/**
 * Lean direction: -1 = lean left, +1 = lean right, 0 = no lean.
 * Duck multiplier: 0 = standing, 1 = full crouch.
 */
interface LeanInfo {
  lean: number;   // -1..+1 horizontal lean
  duck: number;   // 0..1 crouch amount
  urgency: number; // 0..1 how close the obstacle is (1 = imminent)
}

/**
 * Map wall_type to lean direction.
 * Player must dodge AWAY from the wall:
 *   wall_left / angle_left → lean RIGHT (+1)
 *   wall_right / angle_right → lean LEFT (-1)
 *   center / crouch → duck down (lean = 0)
 */
function wallTypeToLean(wallType: string): { lean: number; duck: number } {
  switch (wallType) {
    case 'wall_left':   return { lean: -1, duck: 0 };
    case 'angle_left':  return { lean: -1, duck: 0 };
    case 'wall_right':  return { lean:  1, duck: 0 };
    case 'angle_right': return { lean:  1, duck: 0 };
    case 'center':      return { lean:  1, duck: 0 };
    case 'crouch':      return { lean:  0, duck: 1 };
    default:            return { lean:  0, duck: 0 };
  }
}

/**
 * Binary search for slides near a target beat (slides sorted by time_beats).
 */
function binarySearchSlideBeat(slides: Slide[], targetBeat: number): number {
  let lo = 0;
  let hi = slides.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    if (slides[mid].time_beats < targetBeat) lo = mid + 1;
    else hi = mid - 1;
  }
  return lo;
}

/**
 * Binary search on a sorted number[] (crouches/jumps are time_ms arrays).
 */
function binarySearchMs(arr: number[], targetMs: number): number {
  let lo = 0;
  let hi = arr.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    if (arr[mid] < targetMs) lo = mid + 1;
    else hi = mid - 1;
  }
  return lo;
}

/**
 * Find the next obstacle within the anticipation window and compute lean info.
 * Checks both slides (wall_left, wall_right, etc.) AND dedicated crouches[].
 * Looks ahead up to `anticipationBeats` and also considers obstacles the
 * mannequin is currently passing through (small look-behind of 0.5 beats).
 */
function computeObstacleLean(
  slides: Slide[],
  crouches: number[],
  currentBeat: number,
  bpm: number,
  anticipationBeats: number,
): LeanInfo {
  const lookBehind = 0.5;    // stay leaned/crouched slightly past the obstacle
  const lookAhead = anticipationBeats + 2;

  const searchStart = currentBeat - lookBehind;
  const searchEnd = currentBeat + lookAhead;

  let closestLean = 0;
  let closestDuck = 0;
  let closestUrgency = 0;

  // Helper to evaluate urgency and update best match
  const evaluate = (beat: number, lean: number, duck: number) => {
    const distance = beat - currentBeat;
    let urgency: number;
    if (distance >= 0) {
      urgency = 1 - Math.min(distance / lookAhead, 1);
      urgency = urgency * urgency; // ease-in
    } else {
      urgency = 1 - Math.min(Math.abs(distance) / lookBehind, 1);
    }
    if (urgency > closestUrgency) {
      closestLean = lean;
      closestDuck = duck;
      closestUrgency = urgency;
    }
  };

  // ── Check slides (walls, diagonals, center, crouch-type slides) ──
  if (slides.length > 0) {
    const startIdx = binarySearchSlideBeat(slides, searchStart);
    for (let i = startIdx; i < slides.length; i++) {
      const slide = slides[i];
      if (slide.time_beats > searchEnd) break;
      const { lean, duck } = wallTypeToLean(slide.wall_type);
      if (lean === 0 && duck === 0) continue;
      evaluate(slide.time_beats, lean, duck);
    }
  }

  // ── Check dedicated crouches[] array (time_ms values) ──
  if (crouches.length > 0) {
    const beatsPerMs = bpm / 60000;
    const searchStartMs = searchStart / beatsPerMs;
    const searchEndMs = searchEnd / beatsPerMs;
    const startIdx = binarySearchMs(crouches, searchStartMs);
    for (let i = startIdx; i < crouches.length; i++) {
      const ms = crouches[i];
      if (ms > searchEndMs) break;
      const crouchBeat = ms * beatsPerMs;
      evaluate(crouchBeat, 0, 1); // pure duck
    }
  }

  return { lean: closestLean, duck: closestDuck, urgency: closestUrgency };
}

// ── Rail Position Interpolation ─────────────────────────────────────────────

/**
 * For a note with a rail, compute the hand position along the rail at the
 * current beat. The note head is the first point, then rail_nodes follow.
 * Returns world-space XY at Z=0 (the playhead plane).
 *
 * Rail node format: [x, y, encodedTime] where encodedTime = timeMs * 0.02
 * So timeMs = node[2] / 0.02
 */
function getRailPositionAtBeat(
  note: Note,
  currentBeat: number,
  bpm: number,
): THREE.Vector3 | null {
  if (!note.has_rail || !note.rail_nodes || note.rail_nodes.length < 1) return null;

  // Build an array of { beat, x, y } for all rail points
  const headBeat = note.time_beats;
  const points: { beat: number; x: number; y: number }[] = [
    {
      beat: headBeat,
      x: note.position[0] * GRID_SCALE * 10,
      y: note.position[1] * GRID_SCALE * 10 + NOTE_Y_OFFSET,
    },
  ];

  for (const node of note.rail_nodes) {
    const nodeTimeMs = node[2] / 0.02;
    const nodeBeats = (nodeTimeMs / 1000.0) * (bpm / 60.0);
    points.push({
      beat: nodeBeats,
      x: node[0] * GRID_SCALE * 10,
      y: node[1] * GRID_SCALE * 10 + NOTE_Y_OFFSET,
    });
  }

  // Find where currentBeat falls in the rail
  const lastPoint = points[points.length - 1];

  // Before rail starts — use note head position
  if (currentBeat <= headBeat) {
    return new THREE.Vector3(points[0].x, points[0].y, 0);
  }

  // After rail ends — return null (rail is done, hand can move on)
  if (currentBeat >= lastPoint.beat) {
    return null;
  }

  // Interpolate between rail segments
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    if (currentBeat >= a.beat && currentBeat < b.beat) {
      const duration = b.beat - a.beat;
      if (duration <= 0) continue;
      const t = (currentBeat - a.beat) / duration;
      return new THREE.Vector3(
        a.x + (b.x - a.x) * t,
        a.y + (b.y - a.y) * t,
        0,
      );
    }
  }

  return new THREE.Vector3(lastPoint.x, lastPoint.y, 0);
}

// ── Synth Console: foot-step lookup ─────────────────────────────────────────
// A note low on the grid (Y ≤ -0.5) is a FOOT note — the same rule TunnelScene's
// NoteMesh uses (isFoot). These helpers are console-only; the caller gates on
// activeView === 'console'. The feet step on these notes (lift + plant), and a
// foot rail keeps the foot planted until the rail ends.

const FOOT_Y_THRESHOLD = -0.5;

function isFootNote(note: Note): boolean {
  return note.position[1] <= FOOT_Y_THRESHOLD;
}

/** Which foot a foot-note drives: by X sign (left of centre = left foot), falling
 *  back to the hand (centred/special foot notes carry hand='special'). */
function footSide(note: Note): 'left' | 'right' {
  const x = note.position[0];
  if (x < -0.02) return 'left';
  if (x > 0.02) return 'right';
  return note.hand === 'left' ? 'left' : 'right';
}

/** Next upcoming FOOT note for a foot side (mirror of findNextNoteForHand). */
function findNextFootNote(
  notes: Note[], currentBeat: number, side: 'left' | 'right', anticipationBeats: number,
): Note | null {
  const searchEnd = currentBeat + anticipationBeats + 4;
  const startIdx = binarySearchBeat(notes, currentBeat - 0.15);
  for (let i = startIdx; i < notes.length; i++) {
    const note = notes[i];
    if (note.time_beats > searchEnd) break;
    if (isFootNote(note) && footSide(note) === side) return note;
  }
  return null;
}

/** A FOOT note whose rail is still active for a side (mirror of findActiveRailNote). */
function findActiveFootRail(
  notes: Note[], currentBeat: number, side: 'left' | 'right', bpm: number,
): Note | null {
  const startIdx = binarySearchBeat(notes, currentBeat);
  for (let i = startIdx - 1; i >= 0; i--) {
    const note = notes[i];
    if (currentBeat - note.time_beats > 16) break;
    if (!note.has_rail || !note.rail_nodes || note.rail_nodes.length === 0) continue;
    if (!isFootNote(note) || footSide(note) !== side) continue;
    const lastNode = note.rail_nodes[note.rail_nodes.length - 1];
    const lastNodeBeat = (lastNode[2] / 0.02 / 1000) * (bpm / 60);
    if (lastNodeBeat > currentBeat) return note;
  }
  return null;
}

/**
 * Step envelope for ONE foot: how far to move laterally (`stepX`, an additive
 * offset from the foot's rest X) and how high to lift (`lift`, 0..1).
 *  • Railed foot note → foot stays PLANTED on the rail's column (tracking its X)
 *    until the rail's last node beat, pressed lightly down.
 *  • Plain foot note → a lift-and-plant arc: the foot rises before the beat and
 *    lands (plants) exactly ON the note's beat, then eases back to the stance.
 * Uses the raw beat (caller passes currentBeat = currentTime/60*bpm).
 */
function computeFootStep(
  notes: Note[], currentBeat: number, bpm: number,
  side: 'left' | 'right', anticipationBeats: number, restX: number,
): { stepX: number; lift: number } {
  // Active rail takes priority → planted, X tracking the rail until it ends.
  const railNote = findActiveFootRail(notes, currentBeat, side, bpm);
  if (railNote) {
    const railPos = getRailPositionAtBeat(railNote, currentBeat, bpm);
    const targetX = railPos ? railPos.x : railNote.position[0] * GRID_SCALE * 10;
    return { stepX: THREE.MathUtils.clamp(targetX - restX, -0.5, 0.5), lift: 0.05 };
  }
  // Upcoming / just-passed foot note → lift-and-plant arc.
  const note = findNextFootNote(notes, currentBeat, side, anticipationBeats);
  if (!note) return { stepX: 0, lift: 0 };
  const d = currentBeat - note.time_beats;       // <0 ahead, >0 passed
  const ANTI = 0.5, REL = 0.45;
  if (d < -ANTI || d > REL) return { stepX: 0, lift: 0 };
  const targetX = note.position[0] * GRID_SCALE * 10;
  const dx = THREE.MathUtils.clamp(targetX - restX, -0.5, 0.5);
  let progress: number, lift: number;
  if (d <= 0) {
    const t = (d + ANTI) / ANTI;                 // 0..1 approaching the beat
    progress = t * t * (3 - 2 * t);              // smoothstep → at the plant, progress = 1
    lift = Math.sin(Math.PI * t);                // up-then-down arc, 0 at the plant (lands on the beat)
  } else {
    const t = d / REL;                           // 0..1 after the beat
    progress = 1 - t * t * (3 - 2 * t);          // ease the foot back to its stance
    lift = 0;
  }
  return { stepX: dx * progress, lift };
}

// ── Imperative Limb Mesh Update ─────────────────────────────────────────────

/**
 * Position and orient a cylinder mesh between two endpoints.
 * Updates position, quaternion, and Y scale imperatively.
 */
function updateLimbMesh(
  mesh: THREE.Mesh | null,
  start: THREE.Vector3,
  end: THREE.Vector3,
  nominalHeight: number,
): void {
  if (!mesh) return;

  // Position at midpoint
  mesh.position.addVectors(start, end).multiplyScalar(0.5);

  // Orient cylinder (default Y-axis) along start→end
  _tempDir.subVectors(end, start).normalize();
  _tempQuat.setFromUnitVectors(_yAxis, _tempDir);
  mesh.quaternion.copy(_tempQuat);

  // Scale Y to match actual distance
  const actualLen = start.distanceTo(end);
  mesh.scale.set(1, actualLen / nominalHeight, 1);
}

// ── Main Component ──────────────────────────────────────────────────────────

export default function HumanFigure() {
  const groupRef = useRef<THREE.Group>(null);
  const upperBodyRef = useRef<THREE.Group>(null);  // pivots at hips for lean

  // Read mannequin settings from store (triggers re-render on change)
  const mannequinSettings = useEditorStore(s => s.mannequinSettings);
  const BODY = useMemo(() => computeBody(mannequinSettings), [mannequinSettings]);

  // Note colors for arm/hand tinting
  const noteColorLeft = useEditorStore(s => s.gameplaySettings.noteColorLeft);
  const noteColorRight = useEditorStore(s => s.gameplaySettings.noteColorRight);

  // Refs for imperatively animated arm parts
  const leftUpperArmRef = useRef<THREE.Mesh>(null);
  const leftLowerArmRef = useRef<THREE.Mesh>(null);
  const leftHandRef = useRef<THREE.Mesh>(null);
  const rightUpperArmRef = useRef<THREE.Mesh>(null);
  const rightLowerArmRef = useRef<THREE.Mesh>(null);
  const rightHandRef = useRef<THREE.Mesh>(null);

  // Refs for body parts that dance
  const headRef = useRef<THREE.Mesh>(null);
  const shoulderBarRef = useRef<THREE.Mesh>(null);
  const spineRef = useRef<THREE.Mesh>(null);
  const hipBarRef = useRef<THREE.Mesh>(null);

  // Segmented leg refs (thigh + knee + shin + foot per leg)
  const leftUpperLegRef = useRef<THREE.Mesh>(null);
  const leftKneeRef = useRef<THREE.Mesh>(null);
  const leftLowerLegRef = useRef<THREE.Mesh>(null);
  const leftFootRef = useRef<THREE.Mesh>(null);
  const rightUpperLegRef = useRef<THREE.Mesh>(null);
  const rightKneeRef = useRef<THREE.Mesh>(null);
  const rightLowerLegRef = useRef<THREE.Mesh>(null);
  const rightFootRef = useRef<THREE.Mesh>(null);

  // Smoothed hand target positions (persisted across frames)
  const leftHandSmoothed = useRef(REST_LEFT.clone());
  const rightHandSmoothed = useRef(REST_RIGHT.clone());
  const leftHandTarget = useRef(REST_LEFT.clone());
  const rightHandTarget = useRef(REST_RIGHT.clone());

  // Green note hand assignment: persisted across frames
  // Tracks which hand is assigned to the current green note sequence
  const greenHandAssignment = useRef<'left' | 'right' | 'none'>('none');
  // Track the time_ms of the last green note we assigned for — reset when sequence changes
  const lastGreenNoteId = useRef<string>('');

  // Smoothed obstacle lean/duck values (persisted across frames)
  const leanSmoothed = useRef(0);   // -1..+1 horizontal lean
  const duckSmoothed = useRef(0);   // 0..1 crouch amount

  // Accumulated time for dance phase (continuous, not reset)
  const dancePhase = useRef(0);

  // Synth Console foot-step: smoothed per-foot { x lateral offset, y lift } toward
  // the foot note's column (0 = default stance). Console-only; decays to 0 in VR
  // and when paused, so VR / paused poses are byte-for-byte unchanged.
  const leftFootStep = useRef({ x: 0, y: 0 });
  const rightFootStep = useRef({ x: 0, y: 0 });

  // Shoulder positions (recomputed when proportions change)
  const leftShoulder = useMemo(
    () => new THREE.Vector3(-BODY.shoulderWidth, BODY.shoulderY, BODY.figureZ),
    [BODY.shoulderWidth, BODY.shoulderY, BODY.figureZ],
  );
  const rightShoulder = useMemo(
    () => new THREE.Vector3(BODY.shoulderWidth, BODY.shoulderY, BODY.figureZ),
    [BODY.shoulderWidth, BODY.shoulderY, BODY.figureZ],
  );

  // Generate a key to force geometry remount when proportions change
  const bodyKey = useMemo(() =>
    `${BODY.headRadius}-${BODY.headY}-${BODY.neckY}-${BODY.shoulderY}-${BODY.shoulderWidth}-${BODY.hipY}-${BODY.hipWidth}-${BODY.kneeY}-${BODY.torsoRadius}-${BODY.upperArmLength}-${BODY.lowerArmLength}-${BODY.handRadius}`,
    [BODY],
  );

  useFrame((_state, delta) => {
    // Read store imperatively (no React subscription at 60fps)
    const store = useEditorStore.getState();
    const bpm = store.beatmap?.bpm || 120;
    // Use rawBeat — same reference frame as note.time_beats (no offset subtraction).
    // store.playback.currentBeat subtracts the beatmap offset, but note.time_beats
    // is computed as (time_seconds * bpm / 60) without offset, so we must match it.
    const currentBeat = (store.playback.currentTime / 60) * bpm;
    const diff = store.beatmap?.difficulties[store.activeDifficulty];
    const notes = diff?.notes || [];
    const playing = store.playback.playing;

    // Read animation settings imperatively for 60fps performance
    const { anticipationBeats, lerpSpeed } = store.mannequinSettings;
    const body = computeBody(store.mannequinSettings);

    // ── Green note hand assignment ────────────────────────────────────
    const nextGreen = findNextGreenNote(notes, currentBeat, anticipationBeats);
    if (nextGreen) {
      // Use note_id or time_ms as unique identifier for the green note
      const greenId = nextGreen.note_id || `${nextGreen.time_ms}`;
      if (greenId !== lastGreenNoteId.current) {
        // New green note sequence — pick a random hand
        greenHandAssignment.current = Math.random() < 0.5 ? 'left' : 'right';
        lastGreenNoteId.current = greenId;
      }
    } else {
      // No green notes nearby — reset assignment
      if (greenHandAssignment.current !== 'none') {
        greenHandAssignment.current = 'none';
        lastGreenNoteId.current = '';
      }
    }

    // ── Find next notes per hand ──────────────────────────────────────
    // First check for active rails (note head is past but rail still active),
    // then fall back to upcoming notes. Active rails take priority so hands
    // follow the rail path instead of jumping to the next note.

    const leftActiveRail = findActiveRailNote(notes, currentBeat, 'left', bpm, greenHandAssignment.current);
    const rightActiveRail = findActiveRailNote(notes, currentBeat, 'right', bpm, greenHandAssignment.current);

    const leftNote = leftActiveRail || findNextNoteForHand(notes, currentBeat, 'left', anticipationBeats, greenHandAssignment.current);
    const rightNote = rightActiveRail || findNextNoteForHand(notes, currentBeat, 'right', anticipationBeats, greenHandAssignment.current);

    // ── Compute hand targets (with rail following) ────────────────────

    // Left hand target
    if (leftNote) {
      // Check if the note has a rail and the playhead is within rail range
      const railPos = leftNote.has_rail
        ? getRailPositionAtBeat(leftNote, currentBeat, bpm)
        : null;

      if (railPos) {
        leftHandTarget.current.copy(railPos);
      } else {
        leftHandTarget.current.copy(noteToWorld(leftNote));
      }
    } else {
      leftHandTarget.current.copy(REST_LEFT);
    }

    // Right hand target
    if (rightNote) {
      const railPos = rightNote.has_rail
        ? getRailPositionAtBeat(rightNote, currentBeat, bpm)
        : null;

      if (railPos) {
        rightHandTarget.current.copy(railPos);
      } else {
        rightHandTarget.current.copy(noteToWorld(rightNote));
      }
    } else {
      rightHandTarget.current.copy(REST_RIGHT);
    }

    // Smooth interpolation toward target
    const lerpFactor = 1 - Math.exp(-lerpSpeed * delta);
    leftHandSmoothed.current.lerp(leftHandTarget.current, lerpFactor);
    rightHandSmoothed.current.lerp(rightHandTarget.current, lerpFactor);

    // ── Synth Console: foot-step toward FLOOR (foot) notes ───────────────
    // Foot notes (grid Y ≤ -0.5) make the mannequin lift its foot and step on
    // the note's column; a foot rail keeps the foot planted until it ends. The
    // smoothed offsets feed into the per-leg blocks below. Console + playing
    // only — otherwise the offsets decay to 0 so VR/paused poses are unchanged.
    const isConsole = store.activeView === 'console';
    const FOOT_LIFT_HEIGHT = 0.16;   // world units the foot rises at the peak of a step
    let lStep = { stepX: 0, lift: 0 };
    let rStep = { stepX: 0, lift: 0 };
    if (isConsole && playing) {
      lStep = computeFootStep(notes, currentBeat, bpm, 'left', anticipationBeats, -body.hipWidth);
      rStep = computeFootStep(notes, currentBeat, bpm, 'right', anticipationBeats, body.hipWidth);
    }
    leftFootStep.current.x += (lStep.stepX - leftFootStep.current.x) * lerpFactor;
    leftFootStep.current.y += (lStep.lift * FOOT_LIFT_HEIGHT - leftFootStep.current.y) * lerpFactor;
    rightFootStep.current.x += (rStep.stepX - rightFootStep.current.x) * lerpFactor;
    rightFootStep.current.y += (rStep.lift * FOOT_LIFT_HEIGHT - rightFootStep.current.y) * lerpFactor;

    // Solve IK per arm (use imperatively-read body for arm lengths)
    const leftIK = solveTwoBoneIK(
      leftShoulder,
      leftHandSmoothed.current,
      body.upperArmLength,
      body.lowerArmLength,
      true,
    );
    const rightIK = solveTwoBoneIK(
      rightShoulder,
      rightHandSmoothed.current,
      body.upperArmLength,
      body.lowerArmLength,
      false,
    );

    // Update arm meshes imperatively (convert to upper body group local space)
    const hipYOffset = new THREE.Vector3(0, -body.hipY, 0);
    const lShoulderLocal = leftShoulder.clone().add(hipYOffset);
    const lElbowLocal = leftIK.elbow.clone().add(hipYOffset);
    const lHandLocal = leftIK.hand.clone().add(hipYOffset);
    const rShoulderLocal = rightShoulder.clone().add(hipYOffset);
    const rElbowLocal = rightIK.elbow.clone().add(hipYOffset);
    const rHandLocal = rightIK.hand.clone().add(hipYOffset);

    updateLimbMesh(leftUpperArmRef.current, lShoulderLocal, lElbowLocal, body.upperArmLength);
    updateLimbMesh(leftLowerArmRef.current, lElbowLocal, lHandLocal, body.lowerArmLength);
    if (leftHandRef.current) {
      leftHandRef.current.position.copy(lHandLocal);
      const nc = getNoteColorsFromSettings(useEditorStore.getState().gameplaySettings);
      const leftColor = leftNote ? (nc[leftNote.note_type] || nc.left_hand) : nc.left_hand;
      const lMat = leftHandRef.current.material as THREE.MeshStandardMaterial;
      lMat.color.set(leftColor);
      lMat.emissive.set(leftColor);
    }

    updateLimbMesh(rightUpperArmRef.current, rShoulderLocal, rElbowLocal, body.upperArmLength);
    updateLimbMesh(rightLowerArmRef.current, rElbowLocal, rHandLocal, body.lowerArmLength);
    if (rightHandRef.current) {
      rightHandRef.current.position.copy(rHandLocal);
      const nc2 = getNoteColorsFromSettings(useEditorStore.getState().gameplaySettings);
      const rightColor = rightNote ? (nc2[rightNote.note_type] || nc2.right_hand) : nc2.right_hand;
      const rMat = rightHandRef.current.material as THREE.MeshStandardMaterial;
      rMat.color.set(rightColor);
      rMat.emissive.set(rightColor);
    }

    // ── Obstacle lean/dodge computation ──────────────────────────────
    const slides = diff?.slides || [];
    const crouches = diff?.crouches || [];
    const leanInfo = computeObstacleLean(slides, crouches, currentBeat, bpm, anticipationBeats);

    // Smooth the lean and duck values for organic motion
    const leanLerpFactor = 1 - Math.exp(-18 * delta);  // fast snap into dodge poses
    leanSmoothed.current += (leanInfo.lean * leanInfo.urgency - leanSmoothed.current) * leanLerpFactor;
    duckSmoothed.current += (leanInfo.duck * leanInfo.urgency - duckSmoothed.current) * leanLerpFactor;

    // Current effective lean/duck (small values are zeroed to avoid micro-jitter)
    const effLean = Math.abs(leanSmoothed.current) < 0.01 ? 0 : leanSmoothed.current;
    const effDuck = Math.abs(duckSmoothed.current) < 0.01 ? 0 : duckSmoothed.current;

    // ── Apply lean via UPPER BODY GROUP (pivot at hips, legs stay planted) ──
    // Like the red outline: deep tilt from the waist, head far to the side
    if (upperBodyRef.current) {
      const leanAngle = effLean * 0.85;           // Z-rotation ~49° max (deep lean)
      // Forward lean when crouching (like the reference squat pose)
      const crouchForward = effDuck * 0.35;

      upperBodyRef.current.rotation.z = leanAngle;
      upperBodyRef.current.rotation.x = crouchForward;
    }

    // Keep groupRef scale clean (no squish — real knee bending instead)
    if (groupRef.current) {
      groupRef.current.scale.y = 1;
      groupRef.current.position.y = 0;
      groupRef.current.rotation.z = 0;
      groupRef.current.rotation.x = 0;
      groupRef.current.position.x = 0;
    }

    // ── Lean leg spread (opposite leg bends, lean-side leg extends) ──
    const absLean = Math.abs(effLean);
    const leanLegSpread = absLean * 0.12;     // feet spread wider during lean
    const leanKneeBend = absLean * 0.15;      // opposite-side knee bends more
    const leanHipDrop = absLean * 0.08;       // slight hip drop from wide stance

    // ── Crouch parameters (deep squat with actual knee bending) ──
    // effDuck: 0 = standing, 1 = full squat
    const crouchHipDrop = effDuck * 0.55;       // hips drop low toward ankles
    const crouchKneeForward = effDuck * 0.35;   // knees push far forward
    const crouchKneeDrop = effDuck * 0.2;       // knees drop lower

    // ── Beat-synced dance (knees + weight shift) ─────────────────────
    // Only dance when playing; accumulate phase based on BPM
    if (playing && bpm > 0) {
      const beatsPerSec = bpm / 60;
      dancePhase.current += delta * beatsPerSec * Math.PI * 2;

      // Keep phase in reasonable range to avoid floating point issues
      if (dancePhase.current > 1000) dancePhase.current -= 1000;

      const p = dancePhase.current;

      // ── Weight shifting — alternates left/right each beat ──
      const weightPhase = Math.sin(p * 0.5);

      // Dampen dance sway when crouching
      const danceScale = 1 - effDuck * 0.6;

      // ── Body movements ──
      const hipSway = weightPhase * 0.018 * danceScale;
      const headBob = Math.sin(p) * 0.015 * danceScale;
      const headTilt = weightPhase * 0.04 * danceScale;
      const spineLean = Math.sin(p) * 0.008 * danceScale;
      const spineTwist = weightPhase * 0.03 * danceScale;
      const shoulderCounter = -weightPhase * 0.035 * danceScale;

      // ── Per-leg knee bends (dance + crouch combined) ──
      const baseBend = 0.008;
      const extraBend = 0.03;
      const leftBend = baseBend + extraBend * Math.max(0, weightPhase);
      const rightBend = baseBend + extraBend * Math.max(0, -weightPhase);

      // Foot lift suppressed during crouch
      const leftFootLift = Math.max(0, weightPhase) * 0.015 * danceScale;
      const rightFootLift = Math.max(0, -weightPhase) * 0.015 * danceScale;

      // Total vertical drop: dance knee bend + crouch hip drop
      const kneeDrop = (leftBend + rightBend) * 0.5 + crouchHipDrop;

      // ── Upper body group position (drops with hips during crouch) ──
      if (upperBodyRef.current) {
        upperBodyRef.current.position.y = body.hipY - crouchHipDrop;
      }

      // ── Head (relative to upper body group) ──
      if (headRef.current) {
        headRef.current.position.y = body.headY - body.hipY + headBob - (leftBend + rightBend) * 0.25;
        headRef.current.position.x = hipSway * 0.3;
        headRef.current.rotation.z = headTilt;
      }

      // ── Shoulder bar (relative to upper body group) ──
      if (shoulderBarRef.current) {
        shoulderBarRef.current.position.y = body.shoulderY - body.hipY - (leftBend + rightBend) * 0.25;
        shoulderBarRef.current.position.x = hipSway * 0.2;
        shoulderBarRef.current.rotation.y = spineTwist;
        shoulderBarRef.current.rotation.x = shoulderCounter;
      }

      // ── Spine (relative to upper body group) ──
      if (spineRef.current) {
        const midY = (body.shoulderY + body.hipY) / 2;
        spineRef.current.position.y = midY - body.hipY - (leftBend + rightBend) * 0.125;
        spineRef.current.position.z = body.figureZ + spineLean;
        spineRef.current.position.x = hipSway * 0.5;
        spineRef.current.rotation.y = spineTwist * 0.5;
      }

      // ── Hip bar (drops with crouch + lean hip drop) ──
      if (hipBarRef.current) {
        hipBarRef.current.position.x = hipSway;
        hipBarRef.current.position.y = body.hipY - kneeDrop - leanHipDrop;
      }

      // ── Leg segments — real squat bending ──
      const fz = body.figureZ;
      const thighLen = body.hipY - body.kneeY;
      const shinLen = body.kneeY;

      // Left leg — spreads wider + bends more when leaning right (effLean > 0)
      {
        // When leaning right (effLean > 0): left leg is the OPPOSITE side → bends more, spreads out left
        // When leaning left (effLean < 0): left leg is the LEAN side → extends, spreads out left
        const isOppositeSide = effLean > 0;     // left leg is opposite when leaning right

        const spreadX = -leanLegSpread;  // left leg always spreads further left (negative X)
        const extraKnee = isOppositeSide ? leanKneeBend : 0;  // opposite-side knee bends more
        const hipDropExtra = leanHipDrop;

        const hipX = -body.hipWidth + hipSway + spreadX;
        const hipPos = new THREE.Vector3(hipX, body.hipY - kneeDrop - hipDropExtra, fz);
        // Knee pushes forward + drops during crouch + extra bend during lean
        const kneeForward = leftBend * 3.0 + crouchKneeForward + extraKnee;
        // Console foot-step bends the leg at the knee (raise + push forward) so a
        // stepping foot rises rather than sliding flat. fs.y is 0 except on console.
        const lfs = leftFootStep.current;
        const kneePos = new THREE.Vector3(
          hipX,
          body.kneeY - leftBend * 0.5 - crouchKneeDrop - extraKnee * 0.5 + lfs.y * 0.35,
          fz - kneeForward - lfs.y * 0.6,
        );
        const footPos = new THREE.Vector3(hipX + spreadX + lfs.x, leftFootLift + lfs.y, fz);

        updateLimbMesh(leftUpperLegRef.current, hipPos, kneePos, thighLen);
        updateLimbMesh(leftLowerLegRef.current, kneePos, footPos, shinLen);

        if (leftKneeRef.current) {
          leftKneeRef.current.position.copy(kneePos);
        }
        if (leftFootRef.current) {
          leftFootRef.current.position.copy(footPos);
        }
      }

      // Right leg — spreads wider + bends more when leaning left (effLean < 0)
      {
        // When leaning left (effLean < 0): right leg is the OPPOSITE side → bends more, spreads out right
        // When leaning right (effLean > 0): right leg is the LEAN side → extends, spreads out right
        const isOppositeSide = effLean < 0;     // right leg is opposite when leaning left

        const spreadX = leanLegSpread;  // right leg always spreads further right (positive X)
        const extraKnee = isOppositeSide ? leanKneeBend : 0;  // opposite-side knee bends more
        const hipDropExtra = leanHipDrop;

        const hipX = body.hipWidth + hipSway + spreadX;
        const hipPos = new THREE.Vector3(hipX, body.hipY - kneeDrop - hipDropExtra, fz);
        const kneeForward = rightBend * 3.0 + crouchKneeForward + extraKnee;
        const rfs = rightFootStep.current;
        const kneePos = new THREE.Vector3(
          hipX,
          body.kneeY - rightBend * 0.5 - crouchKneeDrop - extraKnee * 0.5 + rfs.y * 0.35,
          fz - kneeForward - rfs.y * 0.6,
        );
        const footPos = new THREE.Vector3(hipX + spreadX + rfs.x, rightFootLift + rfs.y, fz);

        updateLimbMesh(rightUpperLegRef.current, hipPos, kneePos, thighLen);
        updateLimbMesh(rightLowerLegRef.current, kneePos, footPos, shinLen);

        if (rightKneeRef.current) {
          rightKneeRef.current.position.copy(kneePos);
        }
        if (rightFootRef.current) {
          rightFootRef.current.position.copy(footPos);
        }
      }
    } else {
      // ── Static pose when not playing — legs in default positions ──
      // Reset lean/duck when not playing
      leanSmoothed.current *= 0.95;
      duckSmoothed.current *= 0.95;

      // Reset transforms
      if (upperBodyRef.current) {
        upperBodyRef.current.rotation.z = 0;
        upperBodyRef.current.rotation.x = 0;
        upperBodyRef.current.position.y = body.hipY;
      }
      if (groupRef.current) {
        groupRef.current.scale.y = 1;
        groupRef.current.position.y = 0;
        groupRef.current.rotation.z = 0;
        groupRef.current.rotation.x = 0;
        groupRef.current.position.x = 0;
      }

      const fz = body.figureZ;
      const thighLen = body.hipY - body.kneeY;
      const shinLen = body.kneeY;

      // Left leg static (console foot-step offset decays to 0 here → smooth settle)
      {
        const hipPos = new THREE.Vector3(-body.hipWidth, body.hipY, fz);
        const kneePos = new THREE.Vector3(-body.hipWidth, body.kneeY, fz);
        const footPos = new THREE.Vector3(-body.hipWidth + leftFootStep.current.x, leftFootStep.current.y, fz);
        updateLimbMesh(leftUpperLegRef.current, hipPos, kneePos, thighLen);
        updateLimbMesh(leftLowerLegRef.current, kneePos, footPos, shinLen);
        if (leftKneeRef.current) leftKneeRef.current.position.copy(kneePos);
        if (leftFootRef.current) leftFootRef.current.position.copy(footPos);
      }

      // Right leg static
      {
        const hipPos = new THREE.Vector3(body.hipWidth, body.hipY, fz);
        const kneePos = new THREE.Vector3(body.hipWidth, body.kneeY, fz);
        const footPos = new THREE.Vector3(body.hipWidth + rightFootStep.current.x, rightFootStep.current.y, fz);
        updateLimbMesh(rightUpperLegRef.current, hipPos, kneePos, thighLen);
        updateLimbMesh(rightLowerLegRef.current, kneePos, footPos, shinLen);
        if (rightKneeRef.current) rightKneeRef.current.position.copy(kneePos);
        if (rightFootRef.current) rightFootRef.current.position.copy(footPos);
      }
    }
  });

  return (
    <group ref={groupRef} key={bodyKey}>

      {/* ── Upper body group — pivots at hip height for lean ── */}
      <group ref={upperBodyRef} position={[0, BODY.hipY, 0]}>
        {/* All positions inside this group are RELATIVE to hip pivot */}

        {/* Head (wireframe sphere) */}
        <mesh ref={headRef} position={[0, BODY.headY - BODY.hipY, BODY.figureZ]}>
          <sphereGeometry args={[BODY.headRadius, 12, 8]} />
          <meshStandardMaterial
            color="#A78BFA" emissive="#A78BFA" emissiveIntensity={0.3}
            transparent opacity={0.5} wireframe
          />
        </mesh>

        {/* Neck */}
        <mesh position={[0, (BODY.headY + BODY.neckY) / 2 - BODY.hipY, BODY.figureZ]}>
          <cylinderGeometry args={[0.02, 0.02, BODY.headY - BODY.neckY, 4]} />
          <meshStandardMaterial
            color="#7C3AED" emissive="#7C3AED" emissiveIntensity={0.2}
            transparent opacity={0.5}
          />
        </mesh>

        {/* Shoulder bar */}
        <mesh ref={shoulderBarRef} position={[0, BODY.shoulderY - BODY.hipY, BODY.figureZ]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.025, 0.025, BODY.shoulderWidth * 2, 4]} />
          <meshStandardMaterial
            color="#7C3AED" emissive="#7C3AED" emissiveIntensity={0.2}
            transparent opacity={0.5}
          />
        </mesh>

        {/* Spine */}
        <mesh ref={spineRef} position={[0, (BODY.shoulderY + BODY.hipY) / 2 - BODY.hipY, BODY.figureZ]}>
          <cylinderGeometry args={[0.03, 0.025, BODY.shoulderY - BODY.hipY, 4]} />
          <meshStandardMaterial
            color="#7C3AED" emissive="#7C3AED" emissiveIntensity={0.2}
            transparent opacity={0.5}
          />
        </mesh>

        {/* ── Arms (inside upper body group so they lean with torso) ── */}

        {/* Left upper arm */}
        <mesh ref={leftUpperArmRef}>
          <cylinderGeometry args={[BODY.torsoRadius, BODY.torsoRadius, BODY.upperArmLength, 6]} />
          <meshStandardMaterial
            color={noteColorLeft} emissive={noteColorLeft} emissiveIntensity={0.3}
            transparent opacity={0.6}
          />
        </mesh>

        {/* Left lower arm */}
        <mesh ref={leftLowerArmRef}>
          <cylinderGeometry args={[BODY.torsoRadius * 0.8, BODY.torsoRadius * 0.8, BODY.lowerArmLength, 6]} />
          <meshStandardMaterial
            color={noteColorLeft} emissive={noteColorLeft} emissiveIntensity={0.3}
            transparent opacity={0.6}
          />
        </mesh>

        {/* Left hand */}
        <mesh ref={leftHandRef}>
          <sphereGeometry args={[BODY.handRadius, 10, 8]} />
          <meshStandardMaterial
            color={noteColorLeft} emissive={noteColorLeft} emissiveIntensity={0.6}
            transparent opacity={0.7}
          />
        </mesh>

        {/* Right upper arm */}
        <mesh ref={rightUpperArmRef}>
          <cylinderGeometry args={[BODY.torsoRadius, BODY.torsoRadius, BODY.upperArmLength, 6]} />
          <meshStandardMaterial
            color={noteColorRight} emissive={noteColorRight} emissiveIntensity={0.3}
            transparent opacity={0.6}
          />
        </mesh>

        {/* Right lower arm */}
        <mesh ref={rightLowerArmRef}>
          <cylinderGeometry args={[BODY.torsoRadius * 0.8, BODY.torsoRadius * 0.8, BODY.lowerArmLength, 6]} />
          <meshStandardMaterial
            color={noteColorRight} emissive={noteColorRight} emissiveIntensity={0.3}
            transparent opacity={0.6}
          />
        </mesh>

        {/* Right hand */}
        <mesh ref={rightHandRef}>
          <sphereGeometry args={[BODY.handRadius, 10, 8]} />
          <meshStandardMaterial
            color={noteColorRight} emissive={noteColorRight} emissiveIntensity={0.6}
            transparent opacity={0.7}
          />
        </mesh>
      </group>

      {/* ── Lower body (stays planted on ground) ── */}

      {/* Hip bar */}
      <mesh ref={hipBarRef} position={[0, BODY.hipY, BODY.figureZ]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.02, 0.02, BODY.hipWidth * 2, 4]} />
        <meshStandardMaterial
          color="#7C3AED" emissive="#7C3AED" emissiveIntensity={0.2}
          transparent opacity={0.5}
        />
      </mesh>

      {/* ── Left leg (thigh + knee + shin + foot) ── */}
      <mesh ref={leftUpperLegRef}>
        <cylinderGeometry args={[0.028, 0.024, BODY.hipY - BODY.kneeY, 6]} />
        <meshStandardMaterial
          color="#7C3AED" emissive="#7C3AED" emissiveIntensity={0.2}
          transparent opacity={0.45}
        />
      </mesh>
      <mesh ref={leftKneeRef} position={[-BODY.hipWidth, BODY.kneeY, BODY.figureZ]}>
        <sphereGeometry args={[0.032, 8, 6]} />
        <meshStandardMaterial
          color="#9F7AEA" emissive="#9F7AEA" emissiveIntensity={0.3}
          transparent opacity={0.5}
        />
      </mesh>
      <mesh ref={leftLowerLegRef}>
        <cylinderGeometry args={[0.024, 0.02, BODY.kneeY, 6]} />
        <meshStandardMaterial
          color="#7C3AED" emissive="#7C3AED" emissiveIntensity={0.2}
          transparent opacity={0.4}
        />
      </mesh>
      <mesh ref={leftFootRef} position={[-BODY.hipWidth, 0, BODY.figureZ]}>
        <sphereGeometry args={[0.03, 8, 4]} />
        <meshStandardMaterial
          color="#7C3AED" emissive="#7C3AED" emissiveIntensity={0.2}
          transparent opacity={0.4}
        />
      </mesh>

      {/* ── Right leg (thigh + knee + shin + foot) ── */}
      <mesh ref={rightUpperLegRef}>
        <cylinderGeometry args={[0.028, 0.024, BODY.hipY - BODY.kneeY, 6]} />
        <meshStandardMaterial
          color="#7C3AED" emissive="#7C3AED" emissiveIntensity={0.2}
          transparent opacity={0.45}
        />
      </mesh>
      <mesh ref={rightKneeRef} position={[BODY.hipWidth, BODY.kneeY, BODY.figureZ]}>
        <sphereGeometry args={[0.032, 8, 6]} />
        <meshStandardMaterial
          color="#9F7AEA" emissive="#9F7AEA" emissiveIntensity={0.3}
          transparent opacity={0.5}
        />
      </mesh>
      <mesh ref={rightLowerLegRef}>
        <cylinderGeometry args={[0.024, 0.02, BODY.kneeY, 6]} />
        <meshStandardMaterial
          color="#7C3AED" emissive="#7C3AED" emissiveIntensity={0.2}
          transparent opacity={0.4}
        />
      </mesh>
      <mesh ref={rightFootRef} position={[BODY.hipWidth, 0, BODY.figureZ]}>
        <sphereGeometry args={[0.03, 8, 4]} />
        <meshStandardMaterial
          color="#7C3AED" emissive="#7C3AED" emissiveIntensity={0.2}
          transparent opacity={0.4}
        />
      </mesh>

    </group>
  );
}
